import { useQueryClient } from "@tanstack/react-query";
import { filterClearIcon } from "@tql/ui-components-react/kendo/svg-icons";
import { Popover } from "@tql/ui-components-react/kendo/tooltip";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	infoCircle,
	plusCircle,
	refreshCcw_03,
} from "@tql/ui-components-react/untitled/icons";
import { useAtomValue } from "jotai";
import { useRef, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { CustomerSearch } from "~/shared/components/customer-search/customer-search.tsx";
import { DeleteConfirmationModal } from "~/shared/components/delete-confirmation-modal/delete-confirmation-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useCustomerSelection } from "~/shared/hooks/use-customer-selection.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { UnifiedNotificationRecipient } from "~/shared/schemas/shipment-notification-schema.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { MobileNotifications } from "./components/mobile-notifications/mobile-notifications.tsx";
import { NotificationUserModal } from "./components/notification-user-modal/notification-user-modal.tsx";
import { NotificationsGrid } from "./components/notifications-grid/notifications-grid.tsx";
import {
	useCreateNotificationRecipient,
	useDeleteNotificationRecipient,
	useNotificationRecipients,
	useUpdateNotificationRecipient,
} from "./hooks/use-notification-recipients.ts";
import styles from "./index.module.css";

export const Notifications = () => {
	const { isInternal } = useDeploymentType();
	const isMobile = useIsMobile();
	const { activeCustomer, handleSelectCustomer } = useCustomerSelection({
		baseRoute: routes.notifications,
		customerRoute: routes.notificationsWithCustomer,
	});
	const globalSelectedCustomer = useAtomValue(selectedCustomerAtom);

	const customerId = isInternal
		? (activeCustomer?.id ?? 0)
		: (globalSelectedCustomer?.id ?? 0);
	const queryClient = useQueryClient();

	const {
		data: recipients,
		isFetching: isDataFetching,
		isLoading: isDataLoading,
	} = useNotificationRecipients(customerId);
	const { data: currentUser, isLoading: isUserLoading } =
		useGetCurrentUserInfo(isInternal);
	const isAdmin = isInternal || currentUser?.role === "Admin";
	const currentUserEmail = currentUser?.email ?? "";
	const deleteMutation = useDeleteNotificationRecipient(customerId);
	const createMutation = useCreateNotificationRecipient();
	const updateMutation = useUpdateNotificationRecipient();

	const {
		filter,
		handleFilterChange,
		handleClearFilters,
		gridKey,
		skip,
		take,
		handlePageChange,
		isLoadingPreference,
	} = useGridState(GridIdentifiers.Notifications, {
		enableFilter: true,
		enableClearFilters: true,
	});

	const [modalState, setModalState] = useState<{
		isOpen: boolean;
		editItem?: UnifiedNotificationRecipient;
	}>({ isOpen: false });

	const infoAnchor = useRef<HTMLDivElement>(null);
	const [showInfoPopover, setShowInfoPopover] = useState(false);

	const [deleteModalState, setDeleteModalState] = useState<{
		isOpen: boolean;
		itemToDelete?: UnifiedNotificationRecipient;
	}>({ isOpen: false });

	const handleOpenAdd = () => {
		setModalState({ isOpen: true });
	};

	const handleOpenEdit = (item: UnifiedNotificationRecipient) => {
		setModalState({ isOpen: true, editItem: item });
	};

	const handleCloseModal = () => {
		setModalState({ isOpen: false });
	};

	const handleDeleteClick = (item: UnifiedNotificationRecipient) => {
		setDeleteModalState({ isOpen: true, itemToDelete: item });
	};

	const handleConfirmDelete = () => {
		if (deleteModalState.itemToDelete) {
			deleteMutation.mutate(deleteModalState.itemToDelete.id);
		}
		setDeleteModalState({ isOpen: false, itemToDelete: undefined });
	};

	const handleCancelDelete = () => {
		setDeleteModalState({ isOpen: false, itemToDelete: undefined });
	};

	const handleRefresh = () => {
		void queryClient.invalidateQueries({
			queryKey: ["shipmentNotificationRecipients", customerId],
		});
	};

	const isLoading =
		isDataLoading ||
		isDataFetching ||
		isUserLoading ||
		deleteMutation.isPending ||
		createMutation.isPending ||
		updateMutation.isPending;

	return (
		<TraxPageLayout>
			<TraxPageHeader
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Notifications"}
			/>
			<div className={styles.notificationsContainer}>
				{isMobile ? (
					<>
						{isInternal ? (
							<div className={styles.mobileCustomerSearch}>
								<CustomerSearch
									disabled={false}
									onCustomerChange={handleSelectCustomer}
									selectedCustomer={activeCustomer}
									wrapperStyle={{ width: "300px" }}
								/>
							</div>
						) : null}
						<MobileNotifications
							currentUserEmail={currentUserEmail}
							data={recipients ?? []}
							isAdmin={isAdmin}
							loading={isLoading || isLoadingPreference}
							onDelete={handleDeleteClick}
							onEdit={handleOpenEdit}
						/>
					</>
				) : (
					<>
						<div className={styles.headerActions}>
							{isInternal ? (
								<CustomerSearch
									disabled={false}
									onCustomerChange={handleSelectCustomer}
									selectedCustomer={activeCustomer}
									wrapperStyle={{ width: "300px" }}
								/>
							) : (
								<div style={{ flex: 1 }}></div>
							)}
							<div ref={infoAnchor}>
								<Button
									aria-label={"Notification information"}
									onClick={() => {
										setShowInfoPopover(!showInfoPopover);
									}}
									svgIcon={infoCircle}
									variant={"tertiary"}
								/>
							</div>
							<Popover
								anchor={infoAnchor.current}
								animate={false}
								callout={false}
								onClose={() => {
									setShowInfoPopover(false);
								}}
								position={"bottom"}
								show={showInfoPopover}
							>
								<div className={styles.infoPopover}>
									Users can subscribe to receive email notifications for
									specific shipment events for all shipments tendered for this
									Customer Account.
								</div>
							</Popover>
							<Button
								onClick={handleRefresh}
								svgIcon={refreshCcw_03}
								variant={"tertiary"}
							/>
							<Button
								aria-label={"Clear filters"}
								onClick={handleClearFilters}
								svgIcon={filterClearIcon}
								variant={"tertiary"}
							/>
						</div>
						<NotificationsGrid
							currentUserEmail={currentUserEmail}
							data={recipients ?? []}
							filter={filter}
							gridKey={gridKey}
							isAdmin={isAdmin}
							isLoading={isLoading || isLoadingPreference}
							onDelete={handleDeleteClick}
							onEdit={handleOpenEdit}
							onFilterChange={handleFilterChange}
							onPageChange={handlePageChange}
							skip={skip}
							take={take}
						/>
						<div className={styles.saveButton}>
							<Button
								disabled={customerId <= 0}
								onClick={handleOpenAdd}
								svgIcon={plusCircle}
								variant={"primary"}
							>
								Add User
							</Button>
						</div>
					</>
				)}
			</div>
			{isMobile ? (
				<div className={styles.saveButton}>
					<Button
						disabled={customerId <= 0}
						onClick={handleOpenAdd}
						svgIcon={plusCircle}
						variant={"primary"}
					>
						Add User
					</Button>
				</div>
			) : null}
			<NotificationUserModal
				customerId={customerId}
				editItem={modalState.editItem}
				existingEmails={recipients?.map((r) => r.email) ?? []}
				isInternal={isInternal}
				isOpen={modalState.isOpen}
				onClose={handleCloseModal}
			/>
			<DeleteConfirmationModal
				entityType={"notification users"}
				isOpen={deleteModalState.isOpen}
				itemName={
					deleteModalState.itemToDelete
						? `${deleteModalState.itemToDelete.firstName} ${deleteModalState.itemToDelete.lastName}`.trim()
						: ""
				}
				onCancel={handleCancelDelete}
				onConfirm={handleConfirmDelete}
			/>
		</TraxPageLayout>
	);
};
